import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Memo, MemoForm, MemoSearchForm } from 'entities/memo'

type MemosResponse = PagingResponse & {
    results: Memo[]
}

export function useFetchMemosApi(searchForm?: Form<MemoSearchForm>): IndexApiSet<MemosResponse> & { execute: () => void } {
    const apiPath = 'memos/'
    const api = useIndexApi<MemosResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function usePostMemoApi(): ApiSet<BaseResponse> & { execute: (form: Form<MemoForm>) => void } {
    const apiSet = usePostApi<BaseResponse, MemoForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MemoForm>) => {
        const apiPath = `memos/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutMemoApi(): ApiSet<BaseResponse> & { execute: (form: Form<MemoForm>) => void } {
    const apiSet = usePutApi<BaseResponse, MemoForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MemoForm>) => {
        const apiPath = `memos/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutMemosApi(): ApiSet<BaseResponse> & { execute: (form: Form<MemoForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, MemoForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<MemoForm[]>) => {
        const apiPath = `memos/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteMemoApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `memos/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
